import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, MessageSquare, Bot, UserPlus, Zap, TrendingUp, CheckCircle2, Clock, Filter } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid,
  Legend, RadialBarChart, RadialBar, LineChart, Line,
} from "recharts";

const AXIS_STYLE = { fontSize: 11, fill: "#71717a" };
const TOOLTIP_STYLE = {
  background: "#09090b",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "10px",
  fontSize: "12px",
  padding: "8px 12px",
};

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then(r => setData(r.data)); }, []);

  if (!data) return <div className="p-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin"/></div>;
  const k = data.kpis;
  const convData = [{ name: "conv", value: k.conversion_rate, fill: "#10b981" }];

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto" data-testid="analytics-page">
      <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Insights</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-medium">Analytics</h1>
      <p className="text-zinc-400 mt-2 text-sm">Live performance signals across every AI agent you run.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-8">
        <Kpi icon={Bot} label="Agents" value={`${k.automations_active}/${k.automations_total}`} />
        <Kpi icon={MessageSquare} label="Chats" value={k.chats_started} />
        <Kpi icon={CheckCircle2} label="Completed" value={k.chats_completed} />
        <Kpi icon={UserPlus} label="Leads" value={k.leads_captured} />
        <Kpi icon={TrendingUp} label="Conv. Rate" value={`${k.conversion_rate}%`} accent />
        <Kpi icon={Zap} label="Live" value={k.automations_active} />
      </div>

      {/* Row 1: Trend + Conversion radial */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Panel className="lg:col-span-2" title="Chats vs Leads · last 7 days" testid="chart-trend">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.series} margin={{ top: 10, right: 10, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="gL" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.5}/>
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.45}/>
                  <stop offset="100%" stopColor="#60a5fa" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
              <XAxis dataKey="date" stroke="#52525b" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
              <YAxis stroke="#52525b" tick={AXIS_STYLE} allowDecimals={false} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: "rgba(255,255,255,0.08)", strokeWidth: 1 }}/>
              <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }}/>
              <Area type="monotone" dataKey="chats" name="Chats" stroke="#60a5fa" strokeWidth={2.5} fill="url(#gC)" activeDot={{ r: 5 }}/>
              <Area type="monotone" dataKey="leads" name="Leads" stroke="#10b981" strokeWidth={2.5} fill="url(#gL)" activeDot={{ r: 5 }}/>
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Conversion rate" testid="chart-conversion">
          <div className="relative">
            <ResponsiveContainer width="100%" height={230}>
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={convData} startAngle={225} endAngle={-45}>
                <RadialBar dataKey="value" background={{ fill: "rgba(255,255,255,0.04)" }} cornerRadius={999} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                <div className="font-display text-4xl tracking-tighter text-emerald-400">{k.conversion_rate}%</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">chats → leads</div>
              </div>
            </div>
          </div>
          <div className="text-[11px] text-zinc-500 mt-3">
            {k.leads_captured} of {k.chats_started || 0} chats converted into a captured lead.
          </div>
        </Panel>
      </div>

      {/* Row 2: Funnel + Lead stage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Panel title="Drop-off funnel" testid="chart-funnel"
          right={<Filter className="w-3.5 h-3.5 text-zinc-500"/>}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.funnel} layout="vertical" margin={{ top: 10, right: 30, left: 20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false}/>
              <XAxis type="number" stroke="#52525b" tick={AXIS_STYLE} allowDecimals={false} tickLine={false} axisLine={false} />
              <YAxis dataKey="step" type="category" stroke="#52525b" tick={AXIS_STYLE} tickLine={false} axisLine={false} width={90} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(255,255,255,0.03)" }}/>
              <Bar dataKey="count" fill="#10b981" radius={[0, 6, 6, 0]} barSize={22}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Lead stage distribution" testid="chart-stages">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.lead_stage_distribution} margin={{ top: 10, right: 10, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
              <XAxis dataKey="stage" stroke="#52525b" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
              <YAxis stroke="#52525b" tick={AXIS_STYLE} allowDecimals={false} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(255,255,255,0.03)" }}/>
              <Bar dataKey="count" fill="#a3a3a3" radius={[6, 6, 0, 0]} barSize={38}>
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Row 3: Peak hours */}
      <div className="mt-4">
        <Panel title="Peak hours · last 30 days"
          right={<span className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-zinc-500"><Clock className="w-3 h-3"/> local time</span>}
          testid="chart-hours">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.peak_hours} margin={{ top: 10, right: 10, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="gH" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#10b981"/>
                  <stop offset="100%" stopColor="#60a5fa"/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
              <XAxis dataKey="hour" stroke="#52525b" tick={AXIS_STYLE} tickLine={false} axisLine={false}
                tickFormatter={(h) => `${h}:00`}
                interval={2}/>
              <YAxis stroke="#52525b" tick={AXIS_STYLE} allowDecimals={false} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={(h) => `${h}:00`}/>
              <Line type="monotone" dataKey="chats" stroke="url(#gH)" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: "#10b981" }}/>
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, right, className = "", testid, children }) {
  return (
    <div className={`bg-zinc-950 border border-white/10 rounded-xl p-5 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className={`bg-zinc-950 border ${accent ? "border-emerald-500/30" : "border-white/10"} rounded-xl p-4`} data-testid={`kpi-${label}`}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-zinc-500"><Icon className={`w-3.5 h-3.5 ${accent ? "text-emerald-400" : ""}`}/>{label}</div>
      <div className={`font-display text-3xl tracking-tighter mt-2 ${accent ? "text-emerald-400" : ""}`}>{value}</div>
    </div>
  );
}
